// per docs/shape-uc-27-alert-triggers-and-state.md §2 §4
// per docs/caiet-uc-spec.md §UC27-01 §UC-ALRT-01 §UC-ALRT-03 §UC-ALRT-13
// The wireframe's rule file: the state banner reads first, then the saved
// definition in the same four sections the editor uses.
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudButton, MudInfoBox, MudTag } from '@egov-moldova/mud-react';
import { apiClient, apiErrorKey } from '../../app/api-client';
import {
  Breadcrumb,
  DayStatusBand,
  EMPTY_CELL,
  EmptyState,
  PageHeader,
  RouterLink,
  RowActionsMenu,
  formatDateTime,
  useToast,
} from '@simsm/ui-shared';
import { QueryState } from '../../components/QueryState/QueryState';
import { useConfirm } from '../../components/ConfirmDialog/ConfirmDialog';
import { severitySemantic, formatInterval, type RuleDto } from './AlertRulesPage';
import { describeBuilder } from './alert-builder';
import { describeNoReportParam } from './no-report-exceptions';
import { listWatchedGroups, WATCHED_GROUPS_KEY } from './watched-groups-api';

interface NamedDto {
  id: string;
  name: string;
  displayName: string;
}

// The policy as the file names it: where it delivers and how often at most.
interface PolicyDto extends NamedDto {
  channelIds: string[];
  repeatIntervalSeconds: number;
}

interface EventDto {
  firedAtIso: string;
  title: string;
  body: string;
}

function DefinitionRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="simsm-alerts__definition-row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

// The expression params are a small, closed set per kind; rendering them by
// their own key keeps a new kind readable without a code change here.
//
// A `sql` rule's params are `{ templateId, args }`, and `args` is an object: the
// flat `String(value)` this used to do printed the literal key "args" against
// "[object Object]", and printed `templateId` as the bare safelist identifier
// `agg_lot_expired_count`. So the two are unfolded — the query resolves through
// `alerts.query.*`, which the rule editor already uses, and each argument becomes
// a row of its own.
//
// `groupId` is a watched group's uuid, which no screen prints: the group is named (specs/060).
function paramEntries(
  params: Record<string, unknown>,
  translate: (key: string, fallback: string) => string,
  groupName: (groupId: string) => string = (groupId) => groupId,
): { key: string; label: string; value: string }[] {
  const flat: [string, unknown][] = [];
  for (const [key, value] of Object.entries(params)) {
    if (key === 'args' && value !== null && typeof value === 'object' && !Array.isArray(value)) {
      flat.push(...Object.entries(value as Record<string, unknown>));
      continue;
    }
    flat.push([key, value]);
  }
  return flat
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([key, value]) => ({
      key,
      label: translate(`alerts.param.${key}`, key),
      value:
        key === 'templateId'
          ? translate(`alerts.query.${String(value)}`, String(value))
          : key === 'groupId'
            ? groupName(String(value))
            : key === 'scope'
              ? translate(`alerts.param.scope.${String(value)}`, String(value))
              : (describeNoReportParam(key, value, translate) ??
                (Array.isArray(value) ? value.join(', ') : String(value))),
    }))
    .filter((entry) => entry.value.length > 0);
}

export function AlertRuleDetailPage(): JSX.Element {
  const { t } = useTranslate();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const toast = useToast();
  const [actionErrorKey, setActionErrorKey] = useState<string | null>(null);

  const rule = useQuery({
    queryKey: ['admin-alert-rule', id],
    queryFn: () => apiClient.get<RuleDto>(`/admin/alert-rules/${id}`).then((r) => r.data),
    enabled: Boolean(id),
  });

  const templates = useQuery({
    queryKey: ['notification-templates'],
    queryFn: () =>
      apiClient
        .get<{ templates: NamedDto[] }>('/admin/notification-templates')
        .then((r) => r.data.templates),
  });

  const policies = useQuery({
    queryKey: ['notification-policies'],
    queryFn: () =>
      apiClient
        .get<{ policies: PolicyDto[] }>('/admin/notification-policies')
        .then((r) => r.data.policies),
  });

  const channels = useQuery({
    queryKey: ['notification-channels'],
    queryFn: () =>
      apiClient
        .get<{ channels: NamedDto[] }>('/admin/notification-channels')
        .then((r) => r.data.channels),
  });

  // The band's detail when the rule is firing is the newest trigger's own text,
  // as the wireframe's "Astăzi, ora 06:00 · Locații: 3 …" is.
  const lastEvent = useQuery({
    queryKey: ['alert-events', 'latest', id],
    queryFn: () =>
      apiClient
        .get<{
          alerts: EventDto[];
        }>(`/alerts?tab=all&limit=1&ruleId=${encodeURIComponent(id ?? '')}`)
        .then((r) => r.data.alerts[0] ?? null),
    enabled: Boolean(id),
  });

  const remove = useMutation({
    mutationFn: (ruleId: string) => apiClient.delete(`/admin/alert-rules/${ruleId}`),
    onSuccess: () => {
      toast({
        variant: 'info',
        title: t('alerts.toast.rule.deleted'),
        text: rule.data?.displayName ?? '',
        closeLabel: t('common.close'),
      });
      void queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
      navigate('/system-admin/alerts');
    },
    onError: (err: unknown) =>
      setActionErrorKey(apiErrorKey(err, 'alerts.rules.error.delete_failed')),
  });

  const toggle = useMutation({
    mutationFn: (active: boolean) =>
      apiClient.patch<RuleDto>(`/admin/alert-rules/${id}`, { active }).then((r) => r.data),
    onSuccess: (saved) => {
      setActionErrorKey(null);
      toast({
        variant: saved.active ? 'success' : 'info',
        title: saved.active ? t('alerts.toast.rule.activated') : t('alerts.toast.rule.deactivated'),
        text: saved.displayName,
        closeLabel: t('common.close'),
      });
      queryClient.invalidateQueries({ queryKey: ['admin-alert-rule', id] });
      queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
    },
    // Article IV: the switch used to snap back with nothing said when the server
    // refused, which is indistinguishable from never having asked.
    onError: (err: unknown) =>
      setActionErrorKey(apiErrorKey(err, 'alerts.rules.error.toggle_failed')),
  });

  // Same guard as the register: only the deactivating direction asks, and it uses
  // the wireframe's own words (AdminAlerte.tsx:1773,:1793).
  async function toggleActive(rule: RuleDto): Promise<void> {
    if (
      rule.active &&
      !(await confirm({
        title: t('alerts.confirm.deactivate.title'),
        body: t('alerts.confirm.deactivate.body', { name: rule.displayName }),
        confirmLabel: t('alerts.action.deactivate'),
        cancelLabel: t('alerts.confirm.action.cancel'),
        destructive: true,
      }))
    ) {
      return;
    }
    setActionErrorKey(null);
    toggle.mutate(!rule.active);
  }

  // The wireframe's menu on the file: the state switch and the delete, which it
  // asks about first (AdminAlerte.tsx:1579-1594).
  async function deleteRule(target: RuleDto): Promise<void> {
    if (
      !(await confirm({
        title: t('alerts.confirm.delete.title'),
        body: t('alerts.confirm.delete.body', { name: target.displayName }),
        confirmLabel: t('alerts.confirm.action.confirm'),
        cancelLabel: t('alerts.confirm.action.cancel'),
        destructive: true,
      }))
    ) {
      return;
    }
    setActionErrorKey(null);
    remove.mutate(target.id);
  }

  const r = rule.data;
  // The watched groups are read only for a rule that names one, to put the group's name where
  // its uuid is.
  const groups = useQuery({
    queryKey: [WATCHED_GROUPS_KEY],
    queryFn: listWatchedGroups,
    enabled: typeof r?.expression.params['groupId'] === 'string',
  });
  const none = <span className="simsm-alerts__empty">{EMPTY_CELL}</span>;
  const channelName = (channelId: string): string => {
    const channel = channels.data?.find((c) => c.id === channelId);
    return channel ? channel.displayName || channel.name : channelId;
  };
  const params = !r
    ? []
    : r.expression.kind === 'builder'
      ? describeBuilder(r.expression.params, t)
      : paramEntries(
          r.expression.params,
          (key, fallback) => t(key, fallback),
          (groupId) =>
            groups.data?.find((g) => g.id === groupId)?.name ??
            t('watched_groups.rule.group_missing'),
        );
  // The human name, not the snake_case identifier: the fișă names the objects the
  // way the registers now do.
  const template = templates.data?.find((tpl) => tpl.id === r?.templateId);
  const policy = policies.data?.find((p) => p.id === r?.policyId);
  const templateName = template ? template.displayName || template.name : '';
  const policyName = policy ? policy.displayName || policy.name : '';

  return (
    <section className="simsm-page simsm-alerts">
      <QueryState
        query={rule}
        isEmpty={!r}
        empty={
          /* The wireframe's own not-found for an alerts object is
             AdminAlerte.tsx:2721 — the unframed `.empty` card, the object's own
             glyph, "Canalul nu există", no body, and one outlined button back to
             the register it names. A rule's glyph there is a bell
             (AdminAlerte.tsx:3236); AGE's bell is `notification`.
             A deleted rule is not a system error and never was: the red
             notification this replaces reported a stale bookmark the way it
             reports a failed request. `QueryState` still draws the real failure,
             with its Retry — this branch is emptiness only. */
          <EmptyState
            framed={false}
            icon="notification"
            title={t('alerts.detail.not_found.title')}
            body={t('alerts.detail.not_found.body')}
            action={
              <MudButton
                shape="circular"
                variant="neutral"
                size="md"
                onClick={() => navigate('/system-admin/alerts')}
              >
                {t('alerts.detail.not_found.action')}
              </MudButton>
            }
          />
        }
      >
        {r ? (
          <>
            {/* The wireframe's `crumbs` trail (AdminAlerte.tsx:1903-1909) as
                AGE's own breadcrumb, the way the aggregate detail and every
                form page already draw theirs. */}
            <Breadcrumb
              maxVisible={4}
              label={t('alerts.detail.breadcrumb')}
              crumbs={[
                { label: t('alerts.section_title'), to: '/system-admin/alerts' },
                { label: r.displayName },
              ]}
            />

            {actionErrorKey ? (
              <MudInfoBox
                emphasis="strong"
                role="alert"
                variant="error"
                closable
                closeLabel={t('common.close')}
                onMudClose={() => setActionErrorKey(null)}
              >
                {t(actionErrorKey)}
              </MudInfoBox>
            ) : null}

            {/* The wireframe's header (AdminAlerte.tsx:3247-3262): the severity
                beside the name, the triggers as an outlined button, the edit as
                the one filled one, and the rest behind the menu. */}
            <PageHeader
              title={
                <span className="simsm-page-header__title-status">
                  {r.displayName}
                  <MudTag
                    variant="status"
                    semantic={severitySemantic(r.severity)}
                    label={t(`alerts.severity.${r.severity}`)}
                  />
                </span>
              }
              lede={r.description ?? ''}
              actions={
                <>
                  <MudButton
                    shape="circular"
                    variant="strict"
                    appearance="outlined"
                    size="md"
                    onClick={() =>
                      navigate(`/system-admin/alerts/triggers?ruleId=${encodeURIComponent(r.id)}`)
                    }
                  >
                    {t('alerts.section.triggers')}
                  </MudButton>
                  <MudButton
                    shape="circular"
                    variant="primary"
                    size="md"
                    onClick={() => navigate(`/system-admin/alerts/${r.id}/edit`)}
                  >
                    {t('alerts.action.edit')}
                  </MudButton>
                  <RowActionsMenu
                    align="end"
                    label={t('alerts.action.row_menu', { name: r.displayName })}
                    actions={[
                      {
                        label: r.active
                          ? t('alerts.action.deactivate')
                          : t('alerts.action.activate'),
                        disabled: toggle.isPending,
                        onSelect: () => {
                          void toggleActive(r);
                        },
                      },
                      {
                        label: t('alerts.action.delete'),
                        danger: true,
                        separator: true,
                        disabled: remove.isPending,
                        onSelect: () => {
                          void deleteRule(r);
                        },
                      },
                    ]}
                  />
                </>
              }
            />

            {/* The state first (AdminAlerte.tsx:3235-3240): inactive, firing, or
                quiet with when it was last looked at. */}
            {!r.active ? (
              <DayStatusBand
                status="draft"
                icon="notification"
                title={t('alerts.detail.banner.inactive')}
                detail={t('alerts.detail.banner.inactive_note')}
                actions={
                  <MudButton
                    shape="circular"
                    variant="primary"
                    size="md"
                    disabled={toggle.isPending}
                    onClick={() => {
                      void toggleActive(r);
                    }}
                  >
                    {t('alerts.action.activate')}
                  </MudButton>
                }
              />
            ) : r.evaluatedState === 'firing' ? (
              <DayStatusBand
                status="critical"
                title={t('alerts.detail.banner.firing')}
                detail={
                  lastEvent.data
                    ? t('alerts.detail.banner.fired', {
                        when: formatDateTime(lastEvent.data.firedAtIso),
                        detail: lastEvent.data.body || lastEvent.data.title,
                      })
                    : t('alerts.detail.banner.firing_note')
                }
              />
            ) : (
              <DayStatusBand
                status="info"
                icon="checkmark-large"
                title={t('alerts.detail.banner.normal')}
                detail={t('alerts.detail.banner.checked', {
                  last: r.lastFiredAtIso
                    ? formatDateTime(r.lastFiredAtIso)
                    : t('alerts.detail.banner.never'),
                  next: r.nextEvaluationAtIso
                    ? formatDateTime(r.nextEvaluationAtIso)
                    : t('alerts.detail.banner.on_activation'),
                })}
              />
            )}

            <div className="simsm-alerts__definition simsm-page__group">
              <section className="simsm-alerts__definition-section">
                <div className="simsm-alerts__definition-intro">
                  <h2>{t('alerts.section.basics')}</h2>
                  <p>{t('alerts.detail.basics_lede')}</p>
                </div>
                <div className="simsm-alerts__definition-fields">
                  <dl className="simsm-alerts__definition-list">
                    <DefinitionRow label={t('alerts.detail.name')}>{r.displayName}</DefinitionRow>
                    <DefinitionRow label={t('alerts.column.type')}>
                      {t(`alerts.kind.${r.expression.kind}`)}
                    </DefinitionRow>
                    <DefinitionRow label={t('alerts.field.description')}>
                      {r.description || none}
                    </DefinitionRow>
                  </dl>
                </div>
              </section>

              <section className="simsm-alerts__definition-section">
                <div className="simsm-alerts__definition-intro">
                  <h2>{t('alerts.section.condition')}</h2>
                  <p>{t('alerts.detail.condition_lede')}</p>
                </div>
                <div className="simsm-alerts__definition-fields">
                  <dl className="simsm-alerts__definition-list">
                    {params.length > 0 ? (
                      params.map((entry) => (
                        <DefinitionRow key={entry.key} label={entry.label}>
                          {entry.value}
                        </DefinitionRow>
                      ))
                    ) : (
                      <DefinitionRow label={t('alerts.detail.parameters')}>{none}</DefinitionRow>
                    )}
                  </dl>
                </div>
              </section>

              <section className="simsm-alerts__definition-section">
                <div className="simsm-alerts__definition-intro">
                  <h2>{t('alerts.section.evaluation')}</h2>
                  <p>{t('alerts.detail.evaluation_lede')}</p>
                </div>
                <div className="simsm-alerts__definition-fields">
                  <dl className="simsm-alerts__definition-list">
                    <DefinitionRow label={t('alerts.detail.interval')}>
                      {t('alerts.evaluation.every', {
                        interval: formatInterval(r.evalIntervalSeconds, t),
                      })}
                    </DefinitionRow>
                    <DefinitionRow label={t('alerts.detail.min_duration')}>
                      {r.forDurationSeconds > 0
                        ? formatInterval(r.forDurationSeconds, t)
                        : t('alerts.detail.immediately')}
                    </DefinitionRow>
                    <DefinitionRow label={t('alerts.column.severity')}>
                      <MudTag
                        variant="status"
                        size="sm"
                        semantic={severitySemantic(r.severity)}
                        label={t(`alerts.severity.${r.severity}`)}
                      />
                    </DefinitionRow>
                    <DefinitionRow label={t('alerts.detail.next_evaluation')}>
                      {r.nextEvaluationAtIso
                        ? formatDateTime(r.nextEvaluationAtIso)
                        : r.active
                          ? t('alerts.evaluation.on_activation')
                          : none}
                    </DefinitionRow>
                    <DefinitionRow label={t('alerts.column.last_fired')}>
                      {r.lastFiredAtIso ? formatDateTime(r.lastFiredAtIso) : none}
                    </DefinitionRow>
                  </dl>
                  {Object.keys(r.labels).length > 0 ? (
                    <div>
                      <span className="simsm-alerts__definition-label">
                        {t('alerts.detail.labels')}
                      </span>
                      <span className="simsm-alerts__chips">
                        {Object.entries(r.labels).map(([key, value]) => (
                          <MudTag
                            key={key}
                            variant="info"
                            type="outlined"
                            size="sm"
                            label={`${key}=${value}`}
                          />
                        ))}
                      </span>
                    </div>
                  ) : null}
                </div>
              </section>

              <section className="simsm-alerts__definition-section">
                <div className="simsm-alerts__definition-intro">
                  <h2>{t('alerts.section.notification')}</h2>
                  <p>{t('alerts.detail.notification_lede')}</p>
                </div>
                <div className="simsm-alerts__definition-fields">
                  <dl className="simsm-alerts__definition-list">
                    <DefinitionRow label={t('alerts.detail.transformation')}>
                      {r.notifyOnResolve
                        ? t('alerts.detail.transformation.on_fire_and_resolve')
                        : t('alerts.detail.transformation.on_fire')}
                    </DefinitionRow>
                    <DefinitionRow label={t('alerts.field.template')}>
                      {templateName || none}
                    </DefinitionRow>
                    {/* The policy the rule routes through, with where it delivers and
                        how often at most, as the wireframe's `admin-policy-links`. */}
                    <DefinitionRow label={t('alerts.detail.policies')}>
                      {policy ? (
                        <ul className="simsm-alerts__policy-links">
                          <li>
                            <RouterLink
                              to="/system-admin/alert-policies"
                              size="md"
                              variant="strict"
                              underline="hover"
                            >
                              {policyName}
                            </RouterLink>
                            <span className="simsm-alerts__empty">
                              {[
                                ...policy.channelIds.map(channelName),
                                t('alerts.detail.policy_rate', {
                                  interval: formatInterval(policy.repeatIntervalSeconds, t),
                                }),
                              ].join(' · ')}
                            </span>
                          </li>
                        </ul>
                      ) : (
                        none
                      )}
                    </DefinitionRow>
                  </dl>
                </div>
              </section>
            </div>
          </>
        ) : null}
      </QueryState>
    </section>
  );
}
